import { yupResolver } from "@hookform/resolvers/yup";

import { scheduling } from "lobbie";
import { useCallback, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";

import { useDispatch } from "react-redux";
import ConfirmationCode from "src/components/shared/ConfirmationCode";
import FormFieldError from "src/components/shared/forms/FormFieldError";
import PhoneFormController from "src/components/shared/useForm/PhoneFormController";
import { BASE_PUBLIC_API_ROUTE } from "src/constants";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { useLobbiePatient } from "src/hooks/patients/useLobbiePatient";
import { setUser } from "src/redux/actions/userActions";
import { isEmptyObject, isValidPhoneNumber } from "src/utils";
import * as yup from "yup";

const VALIDATION_SCHEMA = yup.object().shape({
    phone: yup.string().required(),
});

interface IProps {
    onSubmit?: () => void;
}

const PatientSchedulingSMSConfirmation: React.FC<IProps> = ({ onSubmit: propsOnSubmit }) => {
    const dispatch = useDispatch();
    const { selfSchedule, selfScheduleSettings, setSelfSchedule, setRoute, getNextRoute } =
        usePatientSelfSchedulingContext();
    //TODO: LOBBIE-3374 Logic rework is needed here. Are we trying to avoid api calls if not needed?
    if (!selfSchedule || !selfScheduleSettings) return null;
    const [user] = useLobbiePatient(selfScheduleSettings.accountPublicUuid);

    const accountPatientId = useMemo(
        () => selfSchedule.accountPatient?.id || null,
        [selfSchedule.accountPatient],
    );
    const patientSchedulingConfirmationData = useMemo(
        () => ({
            accountPublicUuid: selfScheduleSettings.accountPublicUuid,
            accountPatientId: accountPatientId,
            trackingUuid: selfScheduleSettings.trackingUuid,
            send: `${BASE_PUBLIC_API_ROUTE}/patientscheduling/code/send`,
            confirm: `${BASE_PUBLIC_API_ROUTE}/patientscheduling/code/confirm`,
        }),
        [
            selfScheduleSettings.accountPublicUuid,
            accountPatientId,
            selfScheduleSettings.trackingUuid,
        ],
    );

    const {
        control,
        setValue,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm<any>({
        resolver: yupResolver(VALIDATION_SCHEMA),
        defaultValues: {
            phone: selfSchedule.phone,
        },
    });

    const watchPhone = watch("phone");

    const onSubmit = useCallback(
        (data: { phone: string }) => {
            setSelfSchedule({ ...selfSchedule, phone: data.phone, isPhoneConfirmed: false });
        },
        [selfSchedule, setSelfSchedule],
    );

    const handleContinue = useCallback(
        async (e: React.MouseEvent<HTMLFormElement>) => {
            e.preventDefault();
            e.stopPropagation();

            await handleSubmit(onSubmit)();
        },
        [onSubmit, handleSubmit],
    );

    const handleConfirmed = useCallback(
        ({
            userResult,
        }: {
            userResult: scheduling.patient.IPatientAccountPatient & {
                confirmed: boolean;
            };
        }) => {
            let newPatientScheduling = { ...selfSchedule };
            if (!isEmptyObject(userResult.accountPatient)) {
                dispatch(setUser(userResult));
                newPatientScheduling = {
                    ...selfSchedule,
                    accountPatient: userResult.accountPatient,
                    phone: selfSchedule.phone,
                    isPhoneConfirmed: true,
                    email: selfSchedule.email ? selfSchedule.email : null,
                    isEmailConfirmed: userResult.accountPatient?.isEmailConfirmed
                        ? userResult.accountPatient.isEmailConfirmed
                        : false,
                };
            } else if (!userResult.accountPatient && userResult.confirmed) {
                newPatientScheduling = {
                    ...selfSchedule,
                    isPhoneConfirmed: true,
                    phone: selfSchedule.phone,
                };
            }

            if (propsOnSubmit) {
                setSelfSchedule(newPatientScheduling);
                propsOnSubmit();
            } else {
                setSelfSchedule({
                    ...newPatientScheduling,
                });
                setRoute(getNextRoute());
            }
        },
        [selfSchedule, propsOnSubmit, dispatch, setSelfSchedule, setRoute, getNextRoute],
    );

    const logout = useCallback(() => {
        dispatch(setUser(undefined));
    }, [dispatch]);

    const isAllowingSwitch = useMemo(() => {
        if (user?.activated) {
            return false;
        } else {
            return true;
        }
    }, [user?.activated]);

    const handleSwitch = useCallback(() => {
        if (user?.activated) {
            return;
        } else {
            setValue("phone", "");
            setSelfSchedule({ ...selfSchedule, phone: "", isPhoneConfirmed: false });
        }
    }, [user?.activated, setValue, setSelfSchedule, selfSchedule]);

    const phoneError = useMemo(() => errors.phone?.message, [errors]);

    const render = useMemo(() => {
        if (!selfSchedule.phone) {
            return (
                <div className="row">
                    <div className="col">
                        <Form onSubmit={handleContinue}>
                            <h3 className="extra-bold mt-4">Identity Validation</h3>
                            <div className="col px-0 semi-bold gray2">
                                <div>Confirm your identity to</div>
                                <div>complete this booking.</div>
                                <div>&nbsp;</div>
                                <div>If you are booking for someone</div>
                                <div>else as a representative, you can</div>
                                <div>do so after this step.</div>
                            </div>

                            <Form.Group className="mx-0 mt-5">
                                <Form.Label className="bold">
                                    Validate with phone number:
                                </Form.Label>
                                <PhoneFormController
                                    control={control}
                                    name="phone"
                                    id="mobilePhone"
                                    placeholder="Enter your phone number"
                                    skipVerify
                                />

                                <FormFieldError message={phoneError} />
                            </Form.Group>
                            <Button
                                variant="primary"
                                className="my-3 w-100"
                                type="submit"
                                disabled={!isValidPhoneNumber(watchPhone)}
                            >
                                Continue
                            </Button>
                        </Form>
                    </div>
                </div>
            );
        } else {
            return (
                <>
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="extra-bold mt-4">
                                Almost done! Now <br />
                                check your text messages.
                            </h3>
                        </div>
                    </div>

                    <div className="row">
                        <ConfirmationCode
                            phone={selfSchedule.phone}
                            accountPublicUuid={selfScheduleSettings.accountPublicUuid}
                            showRetry={true}
                            showSwitch={isAllowingSwitch}
                            patientSchedulingConfirmationData={patientSchedulingConfirmationData}
                            isWithSchedulingFooter={true}
                            // @ts-ignore
                            callback={handleConfirmed}
                            handleSwitch={handleSwitch}
                            handleCancel={logout}
                        />
                    </div>
                </>
            );
        }
    }, [
        selfSchedule.phone,
        watchPhone,
        handleContinue,
        selfScheduleSettings.accountPublicUuid,
        handleConfirmed,
        handleSwitch,
        isAllowingSwitch,
        patientSchedulingConfirmationData,
        logout,
        control,
        phoneError,
    ]);

    return <div className="col">{render}</div>;
};

export default PatientSchedulingSMSConfirmation;
